import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Loader2,
  FileText,
  Download,
  Sparkles,
  RefreshCw,
  Ruler,
  Layers,
  Palette,
  Scissors,
  Package,
  X,
  Clock3,
  CheckCircle2,
  Eye,
  PenTool,
} from 'lucide-react';
import { generateTechPack } from '../../api/techPack.api';
import { exportTechPackPdf } from '../../utils/techPackExportPdf';

const DEFAULT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const FIT_OPTIONS = [
  { value: '', label: 'Auto (from design)' },
  { value: 'fitted', label: 'Fitted' },
  { value: 'semi-fitted', label: 'Semi-fitted' },
  { value: 'regular', label: 'Regular' },
  { value: 'relaxed', label: 'Relaxed' },
  { value: 'slim', label: 'Slim' },
  { value: 'bodycon', label: 'Bodycon' },
];

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-purple-100 bg-white/80 p-3 sm:p-4">
      <div className="mb-2 flex items-center gap-2">
        {Icon ? <Icon className="h-3.5 w-3.5 text-purple-600" /> : null}
        <h4 className="text-xs font-semibold uppercase tracking-wide text-purple-700">{title}</h4>
      </div>
      <div className="space-y-1.5 text-xs text-gray-700 sm:text-sm">{children}</div>
    </div>
  );
}

function Kv({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <p>
      <span className="font-medium text-gray-900">{label}: </span>
      <span className="text-gray-700">{value}</span>
    </p>
  );
}

export default function TechPackPanel({
  kit,
  mainImage,
  showNotification,
  refreshKits,
}) {
  const [fitPreference, setFitPreference] = useState('');
  const [notes, setNotes] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [techPack, setTechPack] = useState(() => kit?.techPack || null);
  const [meta, setMeta] = useState(() =>
    kit?.techPack?.meta?.modelUsed
      ? {
          modelUsed: kit.techPack.meta.modelUsed,
          processingTime: null,
        }
      : null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');

  const canGenerate = Boolean(mainImage && kit?._id);

  const measurementSizes = useMemo(
    () => techPack?.measurements?.sizes || DEFAULT_SIZES,
    [techPack]
  );

  useEffect(() => {
    setTechPack(kit?.techPack || null);
    setMeta(
      kit?.techPack?.meta?.modelUsed
        ? { modelUsed: kit.techPack.meta.modelUsed, processingTime: null }
        : null
    );
    // Only reset the modal when switching kits — not when the same kit's
    // techPack is refreshed after generation (that would close the modal).
    setIsModalOpen(false);
  }, [kit?._id]);

  useEffect(() => {
    if (!kit?.techPack) return;
    setTechPack(kit.techPack);
    if (kit.techPack?.meta?.modelUsed) {
      setMeta({ modelUsed: kit.techPack.meta.modelUsed, processingTime: null });
    }
  }, [kit?.techPack]);

  // If a previous mobile session lost its connection mid-job, resume polling.
  useEffect(() => {
    const jobId = kit?.techPackGeneration?.jobId;
    const status = kit?.techPackGeneration?.status;
    if (!jobId || !['queued', 'processing'].includes(status) || isGenerating) return undefined;

    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    setIsGenerating(true);
    setGenerationStatus('Resuming…');

    (async () => {
      try {
        const { getTechPackJob } = await import('../../api/techPack.api');
        const deadline = Date.now() + 8 * 60 * 1000;
        while (!cancelled && Date.now() < deadline) {
          const statusRes = await getTechPackJob(jobId, token);
          const job = statusRes?.data?.data;
          if (job?.status === 'completed' && job?.techPack) {
            if (cancelled) return;
            setTechPack(job.techPack);
            setMeta({ modelUsed: job.modelUsed, processingTime: job.processingTime });
            setIsModalOpen(true);
            showNotification?.('Tech pack ready.', 'success');
            if (typeof refreshKits === 'function') {
              Promise.resolve(refreshKits()).catch(() => {});
            }
            return;
          }
          if (job?.status === 'failed') {
            throw new Error(job?.error || 'Tech pack generation failed');
          }
          setGenerationStatus(
            job?.status === 'queued' ? 'Queued…' : 'Analyzing design & drawing flats…'
          );
          await new Promise((r) => setTimeout(r, 3000));
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Resume tech pack poll failed:', error);
        }
      } finally {
        if (!cancelled) {
          setIsGenerating(false);
          setGenerationStatus('');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [kit?._id, kit?.techPackGeneration?.jobId, kit?.techPackGeneration?.status]);

  useEffect(() => {
    if (!isModalOpen && !isGenerating) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !isGenerating) setIsModalOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen, isGenerating]);

  const handleGenerate = async () => {
    if (!mainImage || !kit?._id) {
      showNotification?.('Import a saved kit with a design image first.', 'error');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      showNotification?.('Please log in to generate a tech pack.', 'error');
      return;
    }

    setIsGenerating(true);
    setGenerationStatus('Starting…');
    try {
      // Slim payload — never send the full kit document (large bodies + long
      // sockets are a common source of mobile axios "Network Error").
      const payload = await generateTechPack(
        {
          imageUrl: mainImage,
          kitId: kit._id,
          sizeRange: DEFAULT_SIZES,
          fitPreference: fitPreference || undefined,
          notes: notes.trim() || undefined,
          productCategory: kit?.productDetails?.productCategory,
          productType: kit?.productDetails?.productType,
          styleName: kit?.name,
        },
        token,
        {
          onStatus: ({ status }) => {
            if (status === 'queued') setGenerationStatus('Queued…');
            else if (status === 'processing') setGenerationStatus('Analyzing design & drawing flats…');
            else if (status === 'completed') setGenerationStatus('Finishing…');
          },
        }
      );

      if (!payload?.techPack) {
        throw new Error('No tech pack returned');
      }
      setTechPack(payload.techPack);
      setMeta({
        modelUsed: payload.modelUsed,
        processingTime: payload.processingTime,
      });
      setIsModalOpen(true);
      if (typeof refreshKits === 'function') {
        Promise.resolve(refreshKits()).catch(() => {});
      }
      try {
        window.dispatchEvent(new CustomEvent('fashdeck:credits-updated'));
      } catch (_) {}
      showNotification?.(
        payload.savedToKit
          ? 'Tech pack generated and saved with this kit (3 credits used).'
          : 'Tech pack generated (3 credits used).',
        'success'
      );
    } catch (error) {
      console.error('Tech pack generation failed:', error);
      const isNetwork =
        !error?.response &&
        (error?.message === 'Network Error' ||
          error?.code === 'ERR_NETWORK' ||
          error?.code === 'ECONNABORTED');
      const isCredits =
        error?.response?.status === 403 ||
        error?.response?.data?.code === 'INSUFFICIENT_CREDITS';
      const message = isCredits
        ? error?.response?.data?.message ||
          'Insufficient credits. Tech pack generation requires 3 image credits.'
        : isNetwork
          ? 'Connection interrupted. Generation may still be running — wait a moment, then tap View if the pack appears, or try again.'
          : error?.response?.data?.message ||
            error?.message ||
            'Failed to generate tech pack.';
      showNotification?.(message, 'error');
      if (isCredits) {
        try {
          window.dispatchEvent(new CustomEvent('fashdeck:credits-updated'));
        } catch (_) {}
      }
    } finally {
      setIsGenerating(false);
      setGenerationStatus('');
    }
  };

  const handleExport = async () => {
    if (!techPack) return;
    setIsExporting(true);
    try {
      await exportTechPackPdf(techPack, {
        imageUrl: mainImage,
        fileName: `${(techPack.cover?.styleName || kit?.name || 'tech-pack')
          .replace(/[^\w\-]+/g, '_')
          .slice(0, 50)}_tech_pack.pdf`,
      });
      showNotification?.('Tech pack PDF downloaded.', 'success');
    } catch (error) {
      console.error('Tech pack PDF export failed:', error);
      showNotification?.(error?.message || 'Failed to export PDF.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-3 sm:p-4">
      <div className="rounded-xl border border-purple-100 bg-gradient-to-br from-purple-50 via-white to-pink-50 p-3 sm:p-4">
        <div className="mb-2 flex items-start gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow">
            <FileText className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-gray-900">AI Tech Pack</h3>
            <p className="mt-0.5 text-xs text-gray-600">
              Generate a factory-ready tech pack from the current Studio design using GPT-5.6,
              graded with FashDeck size-chart &amp; ease formulas. Uses 3 image credits.
            </p>
          </div>
        </div>

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <label className="block text-xs font-medium text-gray-700">
            Fit preference
            <select
              value={fitPreference}
              onChange={(e) => setFitPreference(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-xs text-gray-800 outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
            >
              {FIT_OPTIONS.map((opt) => (
                <option key={opt.value || 'auto'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-medium text-gray-700 sm:col-span-1">
            Extra notes (optional)
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. lined bodice, side zip, soft handfeel"
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-xs text-gray-800 outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
            />
          </label>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={!canGenerate || isGenerating}
            onClick={handleGenerate}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 px-3 py-2 text-xs font-semibold text-white shadow-md transition disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isGenerating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : techPack ? (
              <RefreshCw className="h-3.5 w-3.5" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            {isGenerating ? 'Generating…' : techPack ? 'Regenerate (3 credits)' : 'Generate tech pack (3 credits)'}
          </button>

          <button
            type="button"
            disabled={!techPack || isExporting}
            onClick={handleExport}
            className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-white px-3 py-2 text-xs font-semibold text-purple-700 transition hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isExporting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            Export PDF
          </button>
        </div>

        {!canGenerate && (
          <p className="mt-2 text-xs text-amber-700">
            Import a saved kit with a design image to generate and retain its tech pack.
          </p>
        )}
        {meta?.modelUsed && (
          <p className="mt-2 text-[10px] text-gray-500">
            Model: {meta.modelUsed}
            {meta.processingTime != null ? ` · ${(meta.processingTime / 1000).toFixed(1)}s` : ''}
          </p>
        )}
      </div>

      {isGenerating && createPortal(
        <div className="fixed inset-0 z-[130] flex items-center justify-center overflow-hidden bg-slate-950/75 px-4 backdrop-blur-xl">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-32 -top-32 h-80 w-80 animate-pulse rounded-full bg-purple-500/25 blur-3xl" />
            <div className="absolute -bottom-40 -right-24 h-96 w-96 animate-pulse rounded-full bg-pink-500/20 blur-3xl [animation-delay:700ms]" />
          </div>

          <div className="relative w-full max-w-md overflow-hidden rounded-[2rem] border border-white/20 bg-white/95 p-6 text-center shadow-2xl shadow-purple-950/40 sm:p-8">
            <div className="relative mx-auto mb-6 h-28 w-28">
              <div className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-purple-600 border-r-pink-500" />
              <div className="absolute inset-3 animate-[spin_2s_linear_infinite_reverse] rounded-full border-2 border-transparent border-b-fuchsia-500 border-l-violet-400" />
              <div className="absolute inset-6 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-600 via-fuchsia-500 to-pink-500 shadow-xl shadow-purple-300">
                <FileText className="h-8 w-8 text-white" />
              </div>
              <Sparkles className="absolute -right-1 top-2 h-5 w-5 animate-pulse text-pink-500" />
              <Sparkles className="absolute -left-1 bottom-3 h-4 w-4 animate-pulse text-purple-500 [animation-delay:500ms]" />
            </div>

            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-purple-600">
              FashDeck Intelligence
            </p>
            <h3 className="mt-2 text-xl font-bold text-slate-900 sm:text-2xl">
              Crafting your tech pack
            </h3>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-slate-600">
              GPT-5.6 is analyzing the garment, calculating graded measurements, and drawing
              dimensioned flat sketches for your factory-ready tech pack.
            </p>

            <div className="mt-6 grid grid-cols-3 gap-2">
              {[
                ['Design', Scissors],
                ['Grading', Ruler],
                ['BOM', Package],
              ].map(([label, Icon], index) => (
                <div
                  key={label}
                  className="rounded-xl border border-purple-100 bg-purple-50/70 px-2 py-3"
                >
                  <Icon
                    className="mx-auto h-4 w-4 animate-pulse text-purple-600"
                    style={{ animationDelay: `${index * 250}ms` }}
                  />
                  <p className="mt-1.5 text-[10px] font-semibold text-slate-600">{label}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 flex items-center justify-center gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-amber-800">
              <Clock3 className="h-4 w-4 shrink-0" />
              <p className="text-xs font-medium">Please wait — this usually takes 1–2 minutes.</p>
            </div>
            {generationStatus ? (
              <p className="mt-3 text-[11px] font-medium text-purple-700">{generationStatus}</p>
            ) : null}
            <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-purple-100">
              <div className="h-full w-2/3 animate-pulse rounded-full bg-gradient-to-r from-purple-600 via-fuchsia-500 to-pink-500" />
            </div>
          </div>
        </div>,
        document.body
      )}

      {techPack && !isGenerating && (
        <div className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-gradient-to-r from-emerald-50 to-white p-4 shadow-sm">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white shadow-lg shadow-emerald-200">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">
              {techPack.cover?.styleName || 'Tech pack'} is ready
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              Saved with this kit. You can reopen it whenever you return to AI Studio.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white shadow-md transition hover:bg-purple-700"
          >
            <Eye className="h-3.5 w-3.5" />
            View
          </button>
        </div>
      )}

      {techPack && isModalOpen && !isGenerating && createPortal(
        <div className="fixed inset-0 z-[125] flex flex-col bg-slate-100">
          <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur sm:px-6">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-pink-500 text-white shadow-lg shadow-purple-200">
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-slate-900 sm:text-base">
                {techPack.cover?.styleName || 'Tech Pack'}
              </p>
              <p className="text-[11px] text-slate-500">
                {techPack.cover?.styleCode || 'FashDeck Tech Pack'} · Saved with kit
              </p>
            </div>
            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting}
              className="hidden items-center gap-1.5 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-2.5 text-xs font-semibold text-white shadow-md transition disabled:opacity-60 sm:inline-flex"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Export PDF
            </button>
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
              aria-label="Close tech pack"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4 sm:px-6 sm:py-6">
            <div className="mx-auto max-w-5xl space-y-3">
          <SectionCard title="Cover" icon={FileText}>
            <Kv label="Style" value={techPack.cover?.styleName} />
            <Kv label="Code" value={techPack.cover?.styleCode} />
            <Kv label="Season" value={techPack.cover?.season} />
            <Kv label="Type" value={`${techPack.cover?.productCategory || ''} / ${techPack.cover?.productType || ''}`} />
            <Kv label="Fit" value={techPack.cover?.fit} />
            <Kv label="Audience" value={techPack.cover?.targetAudience} />
            {techPack.cover?.description ? (
              <p className="pt-1 text-gray-600">{techPack.cover.description}</p>
            ) : null}
          </SectionCard>

          {techPack.flatSketches?.length ? (
            <SectionCard title="Technical flat sketches" icon={PenTool}>
              <div className="grid gap-3 sm:grid-cols-2">
                {techPack.flatSketches.map((sketch) => (
                  <figure
                    key={sketch.view}
                    className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                  >
                    <img
                      src={sketch.imageUrl}
                      alt={`${sketch.view} technical flat sketch`}
                      className="aspect-[3/4] w-full bg-white object-contain"
                      loading="lazy"
                    />
                    <figcaption className="border-t border-slate-100 px-3 py-2 text-[11px] font-medium text-slate-600">
                      {sketch.label || `${sketch.view === 'front' ? 'Front' : 'Back'} flat`}
                    </figcaption>
                  </figure>
                ))}
              </div>
              <p className="pt-1 text-[11px] text-slate-500">
                Dimensions shown for base size {techPack.measurements?.baseSize || 'M'} in cm.
              </p>
            </SectionCard>
          ) : null}

          <SectionCard title="Design overview" icon={Scissors}>
            <Kv label="Silhouette" value={techPack.designOverview?.silhouette} />
            <Kv label="Neckline" value={techPack.designOverview?.neckline} />
            <Kv label="Sleeves" value={techPack.designOverview?.sleeves} />
            <Kv label="Length" value={techPack.designOverview?.garmentLength} />
            {techPack.designOverview?.keyDetails?.length ? (
              <p>
                <span className="font-medium text-gray-900">Details: </span>
                {techPack.designOverview.keyDetails.join('; ')}
              </p>
            ) : null}
          </SectionCard>

          <SectionCard title="Materials" icon={Layers}>
            {techPack.materials?.shell?.name ? (
              <p>
                <span className="font-medium text-gray-900">Shell: </span>
                {techPack.materials.shell.name}
                {techPack.materials.shell.composition ? ` — ${techPack.materials.shell.composition}` : ''}
                {techPack.materials.shell.weightGsm != null
                  ? ` (${techPack.materials.shell.weightGsm} GSM)`
                  : ''}
              </p>
            ) : null}
            {techPack.materials?.lining?.name ? (
              <Kv label="Lining" value={techPack.materials.lining.name} />
            ) : null}
            {techPack.materials?.trims?.length ? (
              <p>
                <span className="font-medium text-gray-900">Trims: </span>
                {techPack.materials.trims.join(', ')}
              </p>
            ) : null}
          </SectionCard>

          <SectionCard title="Bill of materials" icon={Package}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-[11px] sm:text-xs">
                <thead>
                  <tr className="border-b border-purple-100 text-gray-500">
                    <th className="py-1 pr-2 font-medium">Item</th>
                    <th className="py-1 pr-2 font-medium">Spec</th>
                    <th className="py-1 pr-2 font-medium">Qty</th>
                    <th className="py-1 font-medium">Placement</th>
                  </tr>
                </thead>
                <tbody>
                  {(techPack.billOfMaterials || []).map((row, idx) => (
                    <tr key={`${row.item}-${idx}`} className="border-b border-gray-50 align-top">
                      <td className="py-1.5 pr-2 font-medium text-gray-900">{row.item}</td>
                      <td className="py-1.5 pr-2 text-gray-600">{row.specification}</td>
                      <td className="py-1.5 pr-2 whitespace-nowrap">
                        {row.quantity}
                        {row.unit ? ` ${row.unit}` : ''}
                      </td>
                      <td className="py-1.5 text-gray-600">{row.placement}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>

          <SectionCard title="Colorways" icon={Palette}>
            <div className="flex flex-wrap gap-2">
              {(techPack.colorways || []).map((c, idx) => (
                <div
                  key={`${c.name}-${idx}`}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-2 py-1.5"
                >
                  <span
                    className="h-4 w-4 rounded-full border border-black/10"
                    style={{ backgroundColor: c.hex || '#ddd' }}
                    title={c.hex}
                  />
                  <span className="text-xs font-medium text-gray-800">
                    {c.isPrimary ? '★ ' : ''}
                    {c.name}
                  </span>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="Graded measurements (cm)" icon={Ruler}>
            <p className="mb-2 text-[11px] text-gray-500">
              Base size: {techPack.measurements?.baseSize || '—'}
              {techPack.easeAndGradingNotes?.easeType
                ? ` · Ease: ${techPack.easeAndGradingNotes.easeType}`
                : ''}
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-[11px] sm:text-xs">
                <thead>
                  <tr className="border-b border-purple-100 text-gray-500">
                    <th className="sticky left-0 bg-white py-1 pr-2 font-medium">POM</th>
                    {measurementSizes.map((s) => (
                      <th key={s} className="px-1.5 py-1 font-medium">
                        {s}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(techPack.measurements?.pointsOfMeasure || []).map((pom, idx) => (
                    <tr key={`${pom.code || pom.pom}-${idx}`} className="border-b border-gray-50">
                      <td className="sticky left-0 bg-white py-1.5 pr-2 font-medium text-gray-900">
                        {pom.pom}
                      </td>
                      {measurementSizes.map((s) => (
                        <td key={s} className="px-1.5 py-1.5 tabular-nums text-gray-700">
                          {pom.values?.[s] ?? '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {techPack.easeAndGradingNotes?.easeApplied ? (
              <p className="mt-2 text-[11px] text-gray-500">{techPack.easeAndGradingNotes.easeApplied}</p>
            ) : null}
          </SectionCard>

          {(techPack.construction?.stitchTypes?.length ||
            techPack.construction?.finishing?.length ||
            techPack.factoryNotes?.length) && (
            <SectionCard title="Construction & factory notes" icon={Package}>
              {techPack.construction?.seamAllowanceCm != null ? (
                <Kv label="Seam allowance" value={`${techPack.construction.seamAllowanceCm} cm`} />
              ) : null}
              {techPack.construction?.stitchTypes?.length ? (
                <p>Stitches: {techPack.construction.stitchTypes.join(', ')}</p>
              ) : null}
              {techPack.construction?.finishing?.length ? (
                <p>Finishing: {techPack.construction.finishing.join('; ')}</p>
              ) : null}
              {techPack.factoryNotes?.length ? (
                <ul className="list-disc space-y-0.5 pl-4">
                  {techPack.factoryNotes.map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
              ) : null}
            </SectionCard>
          )}
              <button
                type="button"
                onClick={handleExport}
                disabled={isExporting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition disabled:opacity-60 sm:hidden"
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Export PDF
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
